import * as classnames from 'classnames';
import * as React from 'react';
import { observer } from 'mobx-react';
import { ContentSpacing } from 'elc-base-theme';
import { serviceNames, diContainer } from '../../../service-setup/diContainer';
import styled from 'styled-components';
import { ITranslationsCollection, translate } from 'elc-service';
import { LabelSlide, Accordion } from 'elc-accordion';
import { Subtitle2, Body2 } from 'elc-typography';
import {
    IProductDetailsMapping,
    IProductFullAccordionValues
} from '../../../exported/product-full/ProductFull';
import { ProductUsage } from '../../domain/entities/ProductUsage';
import { ProductFullViewController } from '../../controllers/ProductFullViewController';
import { ProductFullOrderableViewController } from '../../controllers/ProductFullOrderableViewController';
import { IOrderable } from '../../../exported/product-full-order/IProductFullOrderable';
import { IFieldAccordion } from '../../interfaces/IProductFull';
import { FieldType } from '../../constants/FieldType';

interface IProductDetailsAccordion extends IOrderable {
    translations?: ITranslationsCollection;
    productFullFieldAccordion?: IFieldAccordion;
}

interface IProductDataTestId {
    [key: string]: string;
}

interface IAccordionIndex {
    [key: string]: number;
}

const StyledAccordion = styled(Accordion)`
    margin: ${ContentSpacing.space24} 0;
`;

const ProductDetailLabel = styled(Subtitle2)`
    margin: ${ContentSpacing.space16} 0;
`;

const ProductDetailContent = styled(Body2)`
    margin: ${ContentSpacing.space16} 0;
`;

@translate([
    'productDetailsAccordionLabel',
    'productHowToUseAccordionLabel',
    'productWhatItIsAccordionLabel',
    'productWhatItDoesAccordionLabel'
])
@observer
export class ProductDetailsAccordion extends React.Component<IProductDetailsAccordion> {
    private productFullViewController:
        | ProductFullViewController
        | ProductFullOrderableViewController;

    constructor(props: IProductDetailsAccordion) {
        super(props);
        this.productFullViewController = props.isOrderable
            ? diContainer.get(serviceNames.productFullOrderableViewController)
            : diContainer.get(serviceNames.productFullViewController);
    }

    private getAccordionIndex(field: FieldType) {
        const { productFullFieldAccordion } = this.props;

        return productFullFieldAccordion &&
            productFullFieldAccordion[field] &&
            productFullFieldAccordion[field].collapsed
            ? -1
            : 0;
    }

    public render() {
        const productFullAccordionClassNames = classnames(
            'elc-product-full-accordion',
            'js-product-full-accordion'
        );

        const { data } = this.productFullViewController;
        const {
            product: { whatItIs, whatItDoes, description },
            howToUse
        } = data;

        const { productFullFieldAccordion } = this.props;

        const translations = this.props.translations as ITranslationsCollection;
        const {
            productDetailsAccordionLabel,
            productHowToUseAccordionLabel,
            productWhatItIsAccordionLabel,
            productWhatItDoesAccordionLabel
        } = translations;

        const productFullAccordionLabels = [FieldType.DETAILS, FieldType.HOW_TO_USE];

        const productFullAccordionTitle: IProductDataTestId = {
            [FieldType.DETAILS]: productDetailsAccordionLabel,
            [FieldType.HOW_TO_USE]: productHowToUseAccordionLabel
        };

        const detailsSection =
            productFullFieldAccordion &&
            productFullFieldAccordion[FieldType.DETAILS] &&
            productFullFieldAccordion[FieldType.DETAILS].section;

        const useDescription = detailsSection === FieldType.DESCRIPTION ? true : false;
        const productDetails: ProductUsage[] = [whatItIs, whatItDoes];

        const productFullAccordionValues: IProductFullAccordionValues = {
            [FieldType.HOW_TO_USE]: howToUse
        };

        const productDetailsMapping: IProductDetailsMapping = {
            who_is_it_for: productWhatItIsAccordionLabel,
            how_it_works: productWhatItDoesAccordionLabel
        };

        const productDataTestId: IProductDataTestId = {
            [FieldType.DETAILS]: 'spp_product_details',
            [FieldType.HOW_TO_USE]: 'spp_howtouse'
        };

        const accordionOpenIndex: IAccordionIndex = {
            [FieldType.DETAILS]: this.getAccordionIndex(FieldType.DETAILS),
            [FieldType.HOW_TO_USE]: this.getAccordionIndex(FieldType.HOW_TO_USE)
        };

        const productDescriptionHtml = <Body2 dangerouslySetInnerHTML={{ __html: description }} />;

        const productDetailsHtml = productDetails.map((detail, detailIndex) => (
            <React.Fragment key={detailIndex}>
                <ProductDetailLabel>{productDetailsMapping[detail.type]}</ProductDetailLabel>
                <ProductDetailContent>{detail.content}</ProductDetailContent>
            </React.Fragment>
        ));

        return (
            <Body2>
                {productFullAccordionLabels.map((label: string, index: number) => {
                    return (
                        <StyledAccordion
                            className={productFullAccordionClassNames}
                            key={`${index}-${label}`}
                            openIndex={accordionOpenIndex[label]}
                        >
                            <LabelSlide
                                key={`${index}-${label}`}
                                className={`elc-${label}`}
                                id={`elc-${label}`}
                                title={productFullAccordionTitle[label]}
                                dataTestId={`${productDataTestId[label]}`}
                            >
                                {label === FieldType.DETAILS ? (
                                    useDescription ? (
                                        productDescriptionHtml
                                    ) : (
                                        productDetailsHtml
                                    )
                                ) : (
                                    <Body2
                                        dangerouslySetInnerHTML={{
                                            __html: productFullAccordionValues[label]
                                        }}
                                    />
                                )}
                            </LabelSlide>
                        </StyledAccordion>
                    );
                })}
            </Body2>
        );
    }
}
